import React, { useState } from "react";
import { Search, BookOpen, Sparkles, ShieldCheck, ScrollText, Clock, Brain, CheckCircle2, ExternalLink, ChevronRight, AlertTriangle, Info } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

/**
 * AiActivityTimeline — Iter25.c
 *
 * Pain point (d): el usuario quiere evidencia concreta + AUDITABLE de QUÉ hizo
 * la IA en su caso particular.
 *
 * Cada step ahora es CLICKABLE: abre un Sheet (drawer derecha) con la evidencia
 * subyacente real (tabla de comparables SECOP, lista de hallazgos con severidad,
 * artículos de normativa citados, etc.).
 */
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const formatCOP = (n) => {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n);
  } catch { return `$${n}`; }
};

const labelSemaforo = (s) => ({ verde: "Apto", amarillo: "Requiere revisión", rojo: "No Apto", apto: "Apto", posiblemente_apto: "Requiere revisión", no_apto: "No Apto" }[s] || s);
const labelModalidad = (m) => ({ minima_cuantia: "Mínima Cuantía", seleccion_abreviada: "Selección Abreviada", licitacion_publica: "Licitación Pública", concurso_meritos: "Concurso de Méritos", contratacion_directa: "Contratación Directa" }[m] || m);
const sevColor = (s) => ({ alta: "bg-red-100 text-red-700 border-red-200", media: "bg-amber-100 text-amber-700 border-amber-200", baja: "bg-sky-100 text-sky-700 border-sky-200" }[s] || "bg-gray-100 text-gray-700 border-gray-200");

export default function AiActivityTimeline({ level, artifact, extra = {} }) {
  const [drawer, setDrawer] = useState(null); // { type, title, payload }
  if (!artifact) return null;
  const steps = buildSteps(level, artifact, extra, setDrawer);

  return (
    <>
      <div className="border border-gray-200 bg-white rounded-sm overflow-hidden" data-testid={`ai-timeline-${level}`}>
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <Brain className="w-4 h-4 text-brand-burgundy" />
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Transparencia IA</p>
            <p className="text-sm font-semibold text-gray-900">Lo que la IA hizo en tu caso · <span className="text-gray-500 font-normal">click en cada paso para ver evidencia</span></p>
          </div>
        </div>

        <ol className="px-4 py-3 space-y-3">
          {steps.map((step, i) => {
            const isClickable = !!step.onClick;
            const Wrapper = isClickable ? "button" : "div";
            return (
              <li key={i} data-testid={`ai-timeline-${level}-step-${i}`}>
                <Wrapper
                  type={isClickable ? "button" : undefined}
                  onClick={isClickable ? step.onClick : undefined}
                  className={`w-full text-left flex items-start gap-3 ${isClickable ? "group hover:bg-gray-50 rounded-sm -mx-2 px-2 py-1 transition-colors" : ""}`}
                  data-testid={isClickable ? `ai-timeline-${level}-step-${i}-clickable` : undefined}
                >
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${step.done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      {step.done ? <CheckCircle2 className="w-4 h-4" /> : step.icon}
                    </div>
                    {i < steps.length - 1 && <div className={`w-0.5 h-6 mt-1 ${step.done ? 'bg-emerald-200' : 'bg-gray-200'}`} />}
                  </div>
                  <div className="flex-1 pb-1">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                      <span>{step.title}</span>
                      {step.badge && <span className="text-[10px] bg-brand-burgundy/10 text-brand-burgundy px-1.5 py-0.5 rounded-sm font-semibold">{step.badge}</span>}
                      {isClickable && <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-brand-burgundy transition-colors ml-auto" />}
                    </p>
                    {step.detail && <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{step.detail}</p>}
                    {isClickable && <p className="text-[10px] text-brand-burgundy/70 mt-0.5">Click para ver evidencia →</p>}
                  </div>
                </Wrapper>
              </li>
            );
          })}
        </ol>

        {(artifact.model_used || artifact.created_at) && (
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500 flex items-center justify-between flex-wrap gap-2">
            {artifact.model_used && <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Modelo: <code className="text-gray-700">{artifact.model_used}</code></span>}
            {artifact.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Generado: {formatDate(artifact.created_at)}</span>}
          </div>
        )}
      </div>

      <EvidenceDrawer drawer={drawer} onClose={() => setDrawer(null)} />
    </>
  );
}

/**
 * EvidenceDrawer — Renderiza la evidencia según `drawer.type`.
 * Tipos: 'intake' | 'hallazgos' | 'semaforo' | 'comparables' | 'normativa' | 'review' | 'context' | 'docs'
 */
function EvidenceDrawer({ drawer, onClose }) {
  if (!drawer) return <Sheet open={false} onOpenChange={() => {}} />;
  return (
    <Sheet open={!!drawer} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid={`ai-evidence-${drawer.type}`}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Brain className="w-4 h-4 text-brand-burgundy" />{drawer.title}</SheetTitle>
          <SheetDescription>{drawer.subtitle || "Evidencia auditable de lo que la IA encontró en tu caso."}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {drawer.type === "intake" && <IntakeEvidence payload={drawer.payload} />}
          {drawer.type === "hallazgos" && <HallazgosEvidence payload={drawer.payload} />}
          {drawer.type === "semaforo" && <SemaforoEvidence payload={drawer.payload} />}
          {drawer.type === "comparables" && <ComparablesEvidence payload={drawer.payload} />}
          {drawer.type === "normativa" && <NormativaEvidence payload={drawer.payload} />}
          {drawer.type === "review" && <ReviewEvidence payload={drawer.payload} />}
          {drawer.type === "context" && <ContextEvidence payload={drawer.payload} />}
          {drawer.type === "docs" && <DocsEvidence payload={drawer.payload} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const IntakeEvidence = ({ payload }) => (
  <div className="space-y-2 text-sm" data-testid="evidence-intake">
    {payload?.objeto && <Row label="Objeto contractual" value={payload.objeto} />}
    {payload?.valor != null && <Row label="Valor estimado" value={formatCOP(payload.valor)} />}
    {payload?.entidad && <Row label="Entidad" value={payload.entidad} />}
    {payload?.plazo && <Row label="Plazo" value={payload.plazo} />}
    {!payload?.objeto && <p className="text-xs text-gray-500">Sin metadata adicional del intake.</p>}
  </div>
);

const HallazgosEvidence = ({ payload }) => (
  <div className="space-y-2" data-testid="evidence-hallazgos">
    {(!payload?.hallazgos || payload.hallazgos.length === 0) ? (
      <p className="text-xs text-gray-500">No se detectaron hallazgos significativos.</p>
    ) : payload.hallazgos.map((h, i) => (
      <div key={i} className="border border-gray-200 rounded-sm p-3" data-testid={`evidence-hallazgo-${i}`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
            {h.severidad === "alta" ? <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> : <Info className="w-3.5 h-3.5 text-gray-500" />}
            {h.titulo}
          </p>
          {h.severidad && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${sevColor(h.severidad)}`}>{h.severidad}</span>}
        </div>
        {h.detalle && <p className="text-xs text-gray-600 leading-relaxed">{h.detalle}</p>}
        {h.tipo && <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">{h.tipo}</p>}
      </div>
    ))}
  </div>
);

const SemaforoEvidence = ({ payload }) => (
  <div className="space-y-3" data-testid="evidence-semaforo">
    <div className={`rounded-sm p-4 border-2 ${payload?.semaforo === "verde" ? "bg-emerald-50 border-emerald-200" : payload?.semaforo === "rojo" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
      <p className="text-xs uppercase tracking-widest opacity-70 mb-1">Dictamen</p>
      <p className="text-xl font-bold">{labelSemaforo(payload?.semaforo)}</p>
    </div>
    {payload?.modalidad && <Row label="Modalidad sugerida" value={labelModalidad(payload.modalidad)} />}
    {payload?.recomendacion && (
      <div>
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Recomendación</p>
        <p className="text-sm text-gray-700 leading-relaxed">{payload.recomendacion}</p>
      </div>
    )}
  </div>
);

const ComparablesEvidence = ({ payload }) => {
  const items = payload?.items || [];
  if (items.length === 0) return <p className="text-xs text-gray-500" data-testid="evidence-comparables-empty">No se encontraron comparables SECOP almacenados en este artefacto.</p>;
  return (
    <div className="space-y-2" data-testid="evidence-comparables">
      <p className="text-xs text-gray-500">Mostrando {items.length} contrato{items.length > 1 ? "s" : ""} comparable{items.length > 1 ? "s" : ""}:</p>
      <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
        {items.slice(0, 20).map((c, i) => (
          <div key={i} className="p-3 hover:bg-gray-50" data-testid={`evidence-comparable-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm font-medium text-gray-900 line-clamp-2">{c.objeto || c.title || "Contrato"}</p>
              {c.similarity != null && <span className="text-[10px] bg-brand-burgundy/10 text-brand-burgundy px-1.5 py-0.5 rounded-sm font-semibold whitespace-nowrap">{Math.round(c.similarity * 100)}% similar</span>}
            </div>
            <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
              {c.entidad && <span>🏛️ {c.entidad}</span>}
              {c.valor != null && <span>💰 {formatCOP(c.valor)}</span>}
              {(c.fecha || c.fecha_publicacion) && <span>📅 {String(c.fecha || c.fecha_publicacion).slice(0, 10)}</span>}
              {c.modalidad && <span>📋 {labelModalidad(c.modalidad)}</span>}
            </div>
            {c.url && (
              <a href={c.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-burgundy hover:underline mt-1.5" data-testid={`evidence-comparable-${i}-link`}>
                Ver en SECOP <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const NormativaEvidence = ({ payload }) => {
  const norms = Array.isArray(payload?.normas) ? payload.normas : [];
  if (norms.length === 0) {
    return (
      <div className="space-y-2" data-testid="evidence-normativa-empty">
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
          <p className="text-xs text-amber-900 leading-relaxed">
            <strong>No se registró normativa estructurada para este caso.</strong> Los diagnósticos generados antes
            de la actualización de transparencia (Iter25.g) no incluyen el detalle por norma.
            La normativa general usada se documenta en la lista blanca de la app.
          </p>
        </div>
        <a
          href="/normativa-aplicable"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-brand-burgundy hover:underline"
          data-testid="evidence-normativa-whitelist-link"
        >
          Ver lista blanca normativa completa <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="evidence-normativa">
      <p className="text-xs text-gray-500">
        Normativa específica que la IA cruzó contra este caso ({norms.length} norma{norms.length > 1 ? "s" : ""}):
      </p>
      {norms.map((n, i) => {
        const alias = n.alias || n.nombre || "Norma sin identificar";
        const articulos = Array.isArray(n.articulos) ? n.articulos : [];
        const razon = n.razon_aplicacion || n.descripcion || null;
        return (
          <div key={i} className="border border-gray-200 rounded-sm p-3" data-testid={`evidence-norma-${i}`}>
            <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
              <span>{alias}</span>
              {n.tipo && (
                <span className="text-[10px] uppercase tracking-widest bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm border border-gray-200">
                  {n.tipo}
                </span>
              )}
            </p>
            {razon && (
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                <span className="text-[10px] uppercase tracking-widest text-gray-400 mr-1">por qué aplica:</span>
                {razon}
              </p>
            )}
            {articulos.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {articulos.map((a, j) => (
                  <li key={j} className="text-xs text-gray-700 flex gap-1.5">
                    <ScrollText className="w-3 h-3 mt-0.5 text-gray-400 flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      <a
        href="/normativa-aplicable"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[11px] text-brand-burgundy hover:underline mt-1"
        data-testid="evidence-normativa-whitelist-link"
      >
        Ver lista blanca normativa completa <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};

const ReviewEvidence = ({ payload }) => (
  <div className="space-y-3" data-testid="evidence-review">
    <Row label="Revisor asignado" value={payload?.reviewer_email || "Asignación interna pendiente"} />
    {payload?.checklist_total != null && (
      <div>
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Checklist de verificación</p>
        <p className="text-sm">{payload.checklist_completed ?? "?"} de {payload.checklist_total} ítems verificados</p>
        {payload.checklist_completed != null && (
          <div className="w-full bg-gray-100 rounded-full h-2 mt-1.5"><div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${(payload.checklist_completed / payload.checklist_total) * 100}%` }} /></div>
        )}
      </div>
    )}
    <p className="text-xs text-gray-500 leading-relaxed">El revisor humano experto valida cada output de la IA antes de entregártelo. Cualquier hallazgo crítico (alta severidad) requiere corrección antes de la aprobación final.</p>
  </div>
);

const ContextEvidence = ({ payload }) => (
  <div className="space-y-2 text-sm" data-testid="evidence-context">
    <Row label="Versión del intake N0 usada" value={payload?.n0_version || "Última disponible"} />
    <Row label="Versión del Diagnóstico N1 usada" value={payload?.n1_version || "Última aprobada"} />
    <Row label="Comparables SECOP usados" value={`${payload?.comparables_count || 0} contratos`} />
    {payload?.warnings && payload.warnings.length > 0 && (
      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-sm">
        <p className="text-xs font-medium text-amber-900 mb-1">Alertas de validación ({payload.warnings.length})</p>
        <ul className="text-xs text-amber-800 space-y-0.5">{payload.warnings.map((w, i) => <li key={i}>• {w}</li>)}</ul>
      </div>
    )}
  </div>
);

const DocsEvidence = ({ payload }) => {
  const docs = payload?.documents || [];
  return (
    <div className="space-y-2" data-testid="evidence-docs">
      <p className="text-xs text-gray-500">Documentos generados ({docs.length}):</p>
      {docs.map((d, i) => (
        <div key={i} className="border border-gray-200 rounded-sm p-2" data-testid={`evidence-doc-${i}`}>
          <p className="text-sm font-medium text-gray-900">{d.name || d.nombre || `Documento ${i + 1}`}</p>
          {d.descripcion && <p className="text-xs text-gray-600">{d.descripcion}</p>}
        </div>
      ))}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
    <p className="text-sm text-gray-900">{value}</p>
  </div>
);

function buildSteps(level, a, extra, openDrawer) {
  if (level === "n0") {
    const hallazgos = a.hallazgos || [];
    return [
      {
        icon: <Search className="w-3.5 h-3.5" />,
        title: "Leí tu intake completo",
        detail: "Analicé descripción del objeto, valor estimado, tipo de entidad y plazo.",
        done: true,
        onClick: () => openDrawer({ type: "intake", title: "Intake leído por la IA", payload: extra.intake || a.meta?.intake || {} }),
      },
      {
        icon: <BookOpen className="w-3.5 h-3.5" />,
        title: "Comparé contra patrones de inviabilidad jurídica",
        detail: hallazgos.length > 0 ? `Identifiqué ${hallazgos.length} hallazgo${hallazgos.length > 1 ? 's' : ''} relevante${hallazgos.length > 1 ? 's' : ''}.` : "No detecté patrones de objeto vago, fragmentación o ilegalidad evidente.",
        done: true,
        badge: hallazgos.length > 0 ? `${hallazgos.length} hallazgo${hallazgos.length > 1 ? 's' : ''}` : null,
        onClick: () => openDrawer({ type: "hallazgos", title: "Hallazgos detectados", payload: { hallazgos } }),
      },
      {
        icon: <ScrollText className="w-3.5 h-3.5" />,
        title: "Emití dictamen semáforo",
        detail: a.modalidad_sugerida ? `Modalidad sugerida: ${labelModalidad(a.modalidad_sugerida)}.` : "Dictamen emitido con justificación detallada.",
        done: true,
        badge: a.semaforo ? labelSemaforo(a.semaforo) : null,
        onClick: () => openDrawer({ type: "semaforo", title: "Dictamen y recomendación", payload: { semaforo: a.semaforo || a.result, modalidad: a.modalidad_sugerida, recomendacion: a.recomendacion } }),
      },
    ];
  }

  if (level === "n1") {
    const comparables = extra.comparables || a.comparables_snapshot || [];
    const compCount = extra.comparables_count || a.comparables_count_used || comparables.length;
    const warnings = a.validation_warnings || [];
    return [
      {
        icon: <Search className="w-3.5 h-3.5" />,
        title: "Consulté SECOP I y SECOP II",
        detail: compCount > 0 ? `Encontré ${compCount} contrato${compCount > 1 ? 's' : ''} comparable${compCount > 1 ? 's' : ''} del último año (objeto + monto + región similares).` : "Búsqueda en SECOP completada.",
        done: true,
        badge: compCount > 0 ? `${compCount} comparables` : null,
        onClick: () => openDrawer({ type: "comparables", title: "Comparables SECOP encontrados", subtitle: "Contratos del último año con objeto, monto y región similares.", payload: { items: comparables } }),
      },
      {
        icon: <BookOpen className="w-3.5 h-3.5" />,
        title: "Apliqué normativa colombiana",
        detail: "Crucé con Ley 80, Decreto 1082/2015, jurisprudencia del Consejo de Estado y guías de Colombia Compra Eficiente.",
        done: true,
        onClick: () => openDrawer({ type: "normativa", title: "Normativa aplicada", payload: { normas: extra.normas } }),
      },
      {
        icon: <Sparkles className="w-3.5 h-3.5" />,
        title: "Generé borrador de análisis precontractual",
        detail: "Identifiqué modalidad recomendada, riesgos jurídicos y plan de ruta.",
        done: true,
        badge: warnings.length > 0 ? `${warnings.length} alerta${warnings.length > 1 ? 's' : ''}` : null,
        onClick: () => openDrawer({ type: "context", title: "Contexto y validaciones del análisis", payload: { comparables_count: compCount, warnings } }),
      },
      {
        icon: <ShieldCheck className="w-3.5 h-3.5" />,
        title: "Revisor humano experto validó la salida",
        detail: extra.reviewer_email ? `Revisado por ${extra.reviewer_email}.` : "Un abogado experto revisó y aprobó el contenido antes de tu entrega.",
        done: true,
        onClick: () => openDrawer({ type: "review", title: "Revisión humana experta", payload: { reviewer_email: extra.reviewer_email } }),
      },
    ];
  }

  if (level === "n2") {
    const comparables = extra.comparables || a.comparables_snapshot || [];
    const compCount = extra.comparables_count || a.comparables_count_used || comparables.length;
    const vo = a.validated_output || {};
    const docs = Array.isArray(vo.documents) ? vo.documents : Object.keys(vo).filter(k => typeof vo[k] === "object").map(k => ({ name: k, descripcion: "Sección generada" }));
    const checklistDone = extra.checklist_completed;
    const checklistTotal = extra.checklist_total || 10;
    return [
      {
        icon: <Search className="w-3.5 h-3.5" />,
        title: "Reuní el contexto completo del caso",
        detail: "Usé tu intake N0, el Diagnóstico aprobado, los comparables SECOP y tu información N2.",
        done: true,
        badge: compCount > 0 ? `${compCount} comparables` : null,
        onClick: () => openDrawer({ type: "context", title: "Contexto reunido para tu Kit", payload: { comparables_count: compCount, warnings: a.validation_warnings || [] } }),
      },
      {
        icon: <Sparkles className="w-3.5 h-3.5" />,
        title: "Generé el paquete documental",
        detail: docs.length > 0 ? `${docs.length} secciones/documentos generados.` : "Redacté Estudios Previos, Análisis del Sector, Matriz de Riesgos, Especificaciones, Invitación y Checklist legal.",
        done: true,
        onClick: () => openDrawer({ type: "docs", title: "Documentos generados", payload: { documents: docs } }),
      },
      {
        icon: <BookOpen className="w-3.5 h-3.5" />,
        title: "Cité normativa aplicable",
        detail: "Incluí citaciones a Ley 80 art. 24, Decreto 1082, manuales de Colombia Compra Eficiente y jurisprudencia relevante.",
        done: true,
        onClick: () => openDrawer({ type: "normativa", title: "Normativa citada en el Kit", payload: { normas: extra.normas } }),
      },
      {
        icon: <ShieldCheck className="w-3.5 h-3.5" />,
        title: "Revisor humano experto validó 10 checks legales",
        detail: extra.reviewer_email ? `Revisado por ${extra.reviewer_email}. ${checklistDone != null ? `${checklistDone}/${checklistTotal} ítems verificados.` : ""}` : `Un abogado experto verificó los ${checklistTotal} ítems críticos antes de entregarte el ZIP.`,
        done: true,
        badge: checklistDone != null ? `${checklistDone}/${checklistTotal}` : null,
        onClick: () => openDrawer({ type: "review", title: "Revisión humana — checklist legal", payload: { reviewer_email: extra.reviewer_email, checklist_completed: checklistDone, checklist_total: checklistTotal } }),
      },
    ];
  }

  return [];
}
